import React from 'react';
import { Table, Alert, Button, ButtonGroup, Accordion, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx-js-style';
import { formatDate } from '../../utils/dateFormatters';

interface Statement {
  statement_id: string;
  bank_name: string;
  account_number: string;
  statement_date: string;
  beginning_balance: number;
  ending_balance: number;
  total_additions: number;
  total_deductions: number;
  transactions?: Array<{ amount: number; transaction_type: string }>;
  file_id?: string;
  original_filename?: string;
  page_numbers?: Array<number | string>;
  page_number?: number | string;
  case_id?: string;
}

// getAnalysis.py returns raw acct_bank_statements records, which never
// actually populate total_additions/total_deductions (only beginning_balance,
// ending_balance, and the transactions array get stored) - using them
// directly renders as "$NaN" everywhere. Falling back to summing the
// transaction list (which is always present) recovers the real numbers
// instead of just hiding the error.
const getDeposits = (stmt: Statement): number => {
  if (typeof stmt.total_additions === 'number' && !isNaN(stmt.total_additions)) return stmt.total_additions;
  return (stmt.transactions || [])
    .filter(t => t.transaction_type === 'credit')
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
};

const getWithdrawals = (stmt: Statement): number => {
  if (typeof stmt.total_deductions === 'number' && !isNaN(stmt.total_deductions)) return stmt.total_deductions;
  return (stmt.transactions || [])
    .filter(t => t.transaction_type === 'debit')
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
};

const isNum = (v: unknown): v is number => typeof v === 'number' && !isNaN(v);

// Deposits/withdrawals are only trustworthy for imputation if there's a stored
// total or an itemised transaction list to sum.
const activityKnown = (stmt: Statement): boolean =>
  isNum(stmt.total_additions) || isNum(stmt.total_deductions) || !!(stmt.transactions && stmt.transactions.length);

interface ResolvedBalance {
  value: number | null;
  computed: boolean;
}

// The reviewer flagged several rows with a blank beginning/ending balance
// (e.g. the Nov 2011 and Sep 2022 statements) and wants the figure shown, not
// left empty. When one side plus the month's activity is known we can fill the
// other side - labelled "(calc)" so it's never mistaken for the source figure.
const getBeginning = (stmt: Statement): ResolvedBalance => {
  if (isNum(stmt.beginning_balance)) return { value: stmt.beginning_balance, computed: false };
  if (isNum(stmt.ending_balance) && activityKnown(stmt)) {
    return { value: Math.round((stmt.ending_balance - getDeposits(stmt) + getWithdrawals(stmt)) * 100) / 100, computed: true };
  }
  return { value: null, computed: false };
};

const getEnding = (stmt: Statement): ResolvedBalance => {
  if (isNum(stmt.ending_balance)) return { value: stmt.ending_balance, computed: false };
  if (isNum(stmt.beginning_balance) && activityKnown(stmt)) {
    return { value: Math.round((stmt.beginning_balance + getDeposits(stmt) - getWithdrawals(stmt)) * 100) / 100, computed: true };
  }
  return { value: null, computed: false };
};

const balanceText = (b: ResolvedBalance): string =>
  b.value === null ? '' : `${formatCurrency(b.value)}${b.computed ? ' (calc)' : ''}`;

// Source PDF name (minus extension) - the reviewer's "WP" (work paper) column.
const wpRef = (stmt: Statement): string =>
  (stmt.original_filename || '').replace(/\.[^./\\]+$/, '');

const pageRef = (stmt: Statement): string => {
  const pages = (stmt.page_numbers && stmt.page_numbers.length)
    ? stmt.page_numbers
    : (stmt.page_number ? [stmt.page_number] : []);
  return pages.map(String).join(', ');
};

interface MissingStatement {
  bank_name: string;
  account_number: string;
  expected_month: string | null;
  note?: string;
}

interface BalanceGap {
  statement1: { statement_id: string; date: string; ending_balance: number };
  statement2: { statement_id: string; date: string; beginning_balance: number };
  gap: number;
  spans_missing_months?: string[];
}

interface AnalysisNote {
  statement_id?: string;
  date?: string;
  message: string;
}

interface BankStatementGapResultsProps {
  results: {
    statements_analyzed: {
      count: number;
      statement_ids: string[];
    };
    missing_statements: MissingStatement[];
    statement_balance_gaps: BalanceGap[];
    continuity_notes?: AnalysisNote[];
    account_notes?: AnalysisNote[];
    summary: {
      total_missing_statements: number;
      total_balance_gaps: number;
      total_continuity_warnings?: number;
    };
  };
  statements: Statement[];
  caseNumber?: string;
  caseTitle?: string;
  analysisName?: string;
  caseId?: string;
}

interface ExportOptions {
  filename: string;
}

interface TimelineNote {
  type: 'note';
  level: 'warning' | 'info';
  message: string;
}
type TimelineItem = Statement | MissingStatement | { type: 'gap'; gap: BalanceGap } | TimelineNote;

const monthLabel = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const gapMessage = (gap: BalanceGap): string => {
  const base = `Balance Gap Detected: ${formatCurrency(gap.gap)} difference between statements ` +
    `(${formatDate(gap.statement1.date)} ending: ${formatCurrency(gap.statement1.ending_balance)} vs ` +
    `${formatDate(gap.statement2.date)} beginning: ${formatCurrency(gap.statement2.beginning_balance)})`;
  if (gap.spans_missing_months && gap.spans_missing_months.length) {
    return `${base} - spans missing ${gap.spans_missing_months.map(monthLabel).join(', ')} statement(s); ` +
      `the difference is expected to be accounted for there.`;
  }
  return base;
};

// Sorts a group's statements + missing-month entries into one chronological
// timeline, dropping a balance-gap row and any continuity note right after the
// statement it concerns. Shared by the per-account tables and the Excel/CSV export.
const buildTimeline = (
  groupStatements: Statement[],
  groupMissing: MissingStatement[],
  balanceGaps: BalanceGap[],
  notesByStatement: Map<string, AnalysisNote[]>,
): { timeline: TimelineItem[]; trailingNotes: MissingStatement[] } => {
  const datedMissing = groupMissing.filter(m => m.expected_month);
  const trailingNotes = groupMissing.filter(m => !m.expected_month);

  const entries: Array<{ sortKey: string; item: Statement | MissingStatement }> = [
    ...groupStatements.map(s => ({ sortKey: s.statement_date, item: s as Statement | MissingStatement })),
    ...datedMissing.map(m => ({ sortKey: m.expected_month as string, item: m as Statement | MissingStatement })),
  ];
  entries.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  const timeline: TimelineItem[] = [];
  for (const { item } of entries) {
    timeline.push(item);
    if ('statement_id' in item) {
      const gap = balanceGaps.find(g => g.statement1.statement_id === item.statement_id);
      if (gap) timeline.push({ type: 'gap', gap });
      for (const n of notesByStatement.get(item.statement_id) || []) {
        timeline.push({ type: 'note', level: 'warning', message: n.message });
      }
    }
  }
  return { timeline, trailingNotes };
};

const last4 = (accountNumber: string): string => {
  const digits = String(accountNumber || '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : digits;
};

interface AccountGroup {
  key: string;
  bankName: string;
  accountNumber: string;
  statements: Statement[];
  missing: MissingStatement[];
}

// Splits the combined statement/missing-month lists into one group per real
// account (grouped by last-4 account digits, matching how the backend
// analysis itself clusters accounts) so the export reads as one table per
// account, sorted by date within - which is what the reviewer asked for.
const buildAccountGroups = (statements: Statement[], missingStatements: MissingStatement[]): AccountGroup[] => {
  const groups = new Map<string, AccountGroup>();

  const getGroup = (bankName: string, accountNumber: string): AccountGroup => {
    const key = last4(accountNumber) || `${bankName}|${accountNumber}`;
    let group = groups.get(key);
    if (!group) {
      group = { key, bankName, accountNumber, statements: [], missing: [] };
      groups.set(key, group);
    }
    return group;
  };

  for (const m of missingStatements) {
    const group = getGroup(m.bank_name, m.account_number);
    group.bankName = m.bank_name;
    group.accountNumber = m.account_number;
    group.missing.push(m);
  }
  for (const s of statements) {
    const group = getGroup(s.bank_name, s.account_number);
    if (s.account_number.length > group.accountNumber.length) {
      group.accountNumber = s.account_number;
    }
    group.statements.push(s);
  }

  return Array.from(groups.values()).sort((a, b) => b.statements.length - a.statements.length);
};

const EXPORT_HEADERS = ['#', 'WP', 'Page', 'Date', 'Account', 'Beginning Balance',
  'Deposits', 'Withdrawals', 'Net Change', 'Ending Balance', 'Notes'];

type ExportCell = string | number | null;

const BankStatementGapResults: React.FC<BankStatementGapResultsProps> = ({ results, statements, caseNumber, caseTitle, analysisName, caseId }) => {
  const accountGroups = buildAccountGroups(statements, results.missing_statements);

  const notesByStatement = new Map<string, AnalysisNote[]>();
  for (const n of results.continuity_notes || []) {
    if (!n.statement_id) continue;
    const list = notesByStatement.get(n.statement_id) || [];
    list.push(n);
    notesByStatement.set(n.statement_id, list);
  }

  // One flat list of rows for the whole workbook: per-account header rows,
  // then that account's date-sorted timeline, with a single running line
  // number across the entire sheet (data rows only - header rows aren't
  // numbered) so "line 22" is unambiguous when printed.
  type ExportRow = { cells: ExportCell[]; kind: 'account' | 'statement' | 'missing' | 'gap' | 'note' };

  const buildExportRows = (): ExportRow[] => {
    const rows: ExportRow[] = [];
    let line = 0;

    for (const group of accountGroups) {
      rows.push({
        cells: [`${group.bankName} ...${last4(group.accountNumber) || group.accountNumber}`],
        kind: 'account',
      });

      const { timeline, trailingNotes } = buildTimeline(group.statements, group.missing, results.statement_balance_gaps, notesByStatement);

      for (const item of timeline) {
        if ('type' in item && item.type === 'note') {
          rows.push({ cells: ['', '', '', '', '', null, null, null, null, null, item.message], kind: 'note' });
        } else if ('type' in item && item.type === 'gap') {
          line += 1;
          rows.push({ cells: [line, '', '', item.gap.statement1.date, '', null, null, null, null, null, gapMessage(item.gap)], kind: 'gap' });
        } else if ('expected_month' in item) {
          line += 1;
          rows.push({
            cells: [line, '', '', item.expected_month, `${item.bank_name} ${item.account_number}`,
              null, null, null, null, null, 'Missing Statement'],
            kind: 'missing',
          });
        } else {
          const stmt = item as Statement;
          line += 1;
          const beg = getBeginning(stmt);
          const end = getEnding(stmt);
          const deposits = getDeposits(stmt);
          const withdrawals = getWithdrawals(stmt);
          rows.push({
            cells: [line, wpRef(stmt), pageRef(stmt), stmt.statement_date,
              `${stmt.bank_name} ${last4(stmt.account_number)}`,
              beg.value, deposits, withdrawals, deposits - withdrawals, end.value,
              [beg.computed ? 'Beginning balance computed' : '', end.computed ? 'Ending balance computed' : '']
                .filter(Boolean).join('; ')],
            kind: 'statement',
          });
        }
      }

      for (const t of trailingNotes) {
        rows.push({ cells: ['', '', '', '', '', null, null, null, null, null, t.note || 'Missing statements'], kind: 'note' });
      }
    }
    return rows;
  };

  const exportToCSV = ({ filename }: ExportOptions) => {
    const escapeCSV = (value: unknown): string => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const csvRows: ExportCell[][] = [EXPORT_HEADERS];
    for (const row of buildExportRows()) {
      csvRows.push(row.kind === 'account' ? [row.cells[0]] : row.cells);
    }
    const csvContent = csvRows.map(r => r.map(escapeCSV).join(',')).join('\n');
    saveAs(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }), `${filename}.csv`);
  };

  const exportToExcel = ({ filename }: ExportOptions) => {
    const wb = XLSX.utils.book_new();

    const styles = {
      title: { font: { bold: true, sz: 14 } },
      account: { font: { bold: true }, fill: { fgColor: { rgb: 'D9E2F3' } } },
      missing: { fill: { fgColor: { rgb: 'FFEB3B' } } },
      gap: { fill: { fgColor: { rgb: 'FF5252' } } },
      note: { font: { italic: true, color: { rgb: '8A6D00' } } },
      header: { font: { bold: true }, fill: { fgColor: { rgb: 'E0E0E0' } } },
      number: { numFmt: '#,##0.00', alignment: { horizontal: 'right' } },
    };

    const summaryData: ExportCell[][] = [
      ['Bank Statement Gap Analysis'],
      [`Analysis Name: ${analysisName || 'N/A'}`],
      [`Case Title: ${caseTitle || 'N/A'}`],
      [`Case #: ${caseNumber || 'N/A'}`],
      [],
      ['Summary'],
      [`Total Statements Analyzed: ${results.statements_analyzed.count}`],
      [`Missing Statements: ${results.summary.total_missing_statements}`],
      [`Balance Gaps Found: ${results.summary.total_balance_gaps}`],
      [`Continuity Warnings: ${results.summary.total_continuity_warnings ?? 0}`],
      ...(results.account_notes || []).map(n => [n.message]),
      [],
    ];

    const exportRows = buildExportRows();
    const aoa: ExportCell[][] = [
      ...summaryData,
      EXPORT_HEADERS,
      ...exportRows.map(r => r.kind === 'account'
        ? [r.cells[0], '', '', '', '', '', '', '', '', '', '']
        : r.cells),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    const cols = 'ABCDEFGHIJK'.split('');
    const headerRow = summaryData.length + 1; // 1-indexed

    ws['A1'] = { v: aoa[0][0], s: styles.title };

    cols.forEach(c => {
      const ref = `${c}${headerRow}`;
      ws[ref] = ws[ref] || { v: '' };
      ws[ref].s = styles.header;
    });

    exportRows.forEach((row, idx) => {
      const r = headerRow + idx + 1;
      const rowStyle = row.kind === 'account' ? styles.account
        : row.kind === 'missing' ? styles.missing
        : row.kind === 'gap' ? styles.gap
        : row.kind === 'note' ? styles.note
        : null;
      cols.forEach(c => {
        const ref = `${c}${r}`;
        ws[ref] = ws[ref] || { v: '' };
        ws[ref].s = { ...(ws[ref].s || {}) };
        if (rowStyle) ws[ref].s = { ...ws[ref].s, ...rowStyle };
        // Beginning..Ending balance columns (F-J)
        if ('FGHIJ'.includes(c) && typeof ws[ref].v === 'number') {
          ws[ref].s = { ...ws[ref].s, ...styles.number };
        }
      });
    });

    ws['!cols'] = cols.map((_, i) => {
      let w = 10;
      for (let R = range.s.r; R <= range.e.r; R++) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: i })];
        if (cell && cell.v != null) w = Math.max(w, String(cell.v).length * 1.2);
      }
      return { wch: Math.min(w, 70) };
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Gap Analysis');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const groupStats = (group: AccountGroup) => {
    const ids = new Set(group.statements.map(s => s.statement_id));
    return {
      onFile: group.statements.length,
      totalMonths: group.statements.length + group.missing.filter(m => m.expected_month).length,
      missing: group.missing.filter(m => m.expected_month).length,
      gaps: results.statement_balance_gaps.filter(g => ids.has(g.statement1.statement_id)).length,
      warnings: [...ids].reduce((n, id) => n + (notesByStatement.get(id)?.length || 0), 0),
    };
  };

  // Running line number continuous across every account table, matching the
  // export - so a printed page and the on-screen view refer to rows the same way.
  let screenLine = 0;

  const renderAccountTable = (group: AccountGroup) => {
    const { timeline, trailingNotes } = buildTimeline(group.statements, group.missing, results.statement_balance_gaps, notesByStatement);
    const stats = groupStats(group);

    return (
      <Accordion.Item eventKey={group.key} key={group.key}>
        <Accordion.Header>
          <div className="d-flex flex-wrap align-items-baseline gap-2 w-100 pe-2">
            <span className="fw-semibold">
              {group.bankName} <span className="text-muted">...{last4(group.accountNumber) || group.accountNumber}</span>
            </span>
            <span className="text-muted small">
              {stats.onFile} of {stats.totalMonths} expected month{stats.totalMonths === 1 ? '' : 's'} on file
            </span>
            <span className="ms-auto d-flex gap-1">
              {stats.missing > 0 && <Badge bg="warning" text="dark">{stats.missing} missing</Badge>}
              {stats.gaps > 0 && <Badge bg="danger">{stats.gaps} gap{stats.gaps === 1 ? '' : 's'}</Badge>}
              {stats.warnings > 0 && <Badge bg="secondary">{stats.warnings} warning{stats.warnings === 1 ? '' : 's'}</Badge>}
              {stats.missing === 0 && stats.gaps === 0 && stats.warnings === 0 && <Badge bg="success">clean</Badge>}
            </span>
          </div>
        </Accordion.Header>
        <Accordion.Body>
        <Table striped bordered size="sm">
          <thead>
            <tr>
              <th>#</th>
              <th>WP</th>
              <th>Page</th>
              <th>Date</th>
              <th>Account</th>
              <th>Beginning Balance</th>
              <th>Deposits</th>
              <th>Withdrawals</th>
              <th>Net Change</th>
              <th>Ending Balance</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {timeline.map((item, idx) => {
              if ('type' in item && item.type === 'note') {
                return (
                  <tr key={`note-${idx}`} className="table-warning">
                    <td></td>
                    <td colSpan={10} className="small"><em>{item.message}</em></td>
                  </tr>
                );
              }
              if ('type' in item && item.type === 'gap') {
                screenLine += 1;
                return (
                  <tr key={`gap-${idx}`} className="table-danger">
                    <td>{screenLine}</td>
                    <td colSpan={10} className="text-center"><em>{gapMessage(item.gap)}</em></td>
                  </tr>
                );
              }
              if ('expected_month' in item) {
                screenLine += 1;
                return (
                  <tr key={`missing-${idx}`} className="table-warning">
                    <td>{screenLine}</td>
                    <td colSpan={10} className="text-center">
                      <em>Missing Statement: {item.bank_name} {item.account_number} - {item.expected_month}</em>
                    </td>
                  </tr>
                );
              }
              const stmt = item as Statement;
              screenLine += 1;
              const beg = getBeginning(stmt);
              const end = getEnding(stmt);
              const deposits = getDeposits(stmt);
              const withdrawals = getWithdrawals(stmt);
              const netChange = deposits - withdrawals;
              return (
                <tr key={stmt.statement_id}>
                  <td>{screenLine}</td>
                  <td className="small">{wpRef(stmt) || '-'}</td>
                  <td className="small">{pageRef(stmt) || '-'}</td>
                  <td>{formatDate(stmt.statement_date)}</td>
                  <td>{stmt.bank_name} {last4(stmt.account_number)}</td>
                  <td className={beg.computed ? 'text-muted fst-italic' : ''}>{balanceText(beg) || '-'}</td>
                  <td className="text-success">{formatCurrency(deposits)}</td>
                  <td className="text-danger">{formatCurrency(withdrawals)}</td>
                  <td className={netChange >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(netChange)}</td>
                  <td className={end.computed ? 'text-muted fst-italic' : ''}>{balanceText(end) || '-'}</td>
                  <td>
                    {stmt.file_id && caseId ? (
                      <Link to={`/case/${caseId}/file/${stmt.file_id}`}>
                        <Button variant="outline-primary" size="sm">View</Button>
                      </Link>
                    ) : (
                      <span className="text-muted small">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {trailingNotes.map((t, idx) => (
              <tr key={`trailing-${idx}`} className="table-warning">
                <td></td>
                <td colSpan={10} className="small"><em>{t.note || 'Missing statements'}</em></td>
              </tr>
            ))}
          </tbody>
        </Table>
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Statement Timeline</h3>
        <ButtonGroup>
          <Button variant="outline-secondary" onClick={() => exportToCSV({ filename: `gap-analysis-${caseNumber || 'unknown-case'}` })}>
            Export CSV
          </Button>
          <Button variant="outline-secondary" onClick={() => exportToExcel({ filename: `gap-analysis-${caseNumber || 'unknown-case'}` })}>
            Export Excel
          </Button>
        </ButtonGroup>
      </div>

      {(results.account_notes || []).map((n, idx) => (
        <Alert key={`acct-note-${idx}`} variant="secondary" className="py-2 small">{n.message}</Alert>
      ))}

      {accountGroups.length === 0 ? (
        <Alert variant="warning">No accounts found in this analysis.</Alert>
      ) : (
        <Accordion
          alwaysOpen
          defaultActiveKey={accountGroups
            .filter(g => {
              const s = groupStats(g);
              return s.missing > 0 || s.gaps > 0 || s.warnings > 0;
            })
            .map(g => g.key)}
        >
          {accountGroups.map(renderAccountTable)}
        </Accordion>
      )}

      <Alert variant="info">
        Found {results.summary.total_missing_statements} missing statements and {results.summary.total_balance_gaps} balance discrepancies
        {accountGroups.length > 1 && ` across ${accountGroups.length} accounts`}
        {!!results.summary.total_continuity_warnings && `; ${results.summary.total_continuity_warnings} continuity warning(s)`}
      </Alert>
    </div>
  );
};

export default BankStatementGapResults;
